import React, { useEffect, useMemo, useState } from "react";
import { all_routes } from "../../router/all_routes";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import Sidebar from "../../../core/common/sidebar/sidebar";
import API_URLS from "../../../core/common/apiUrls";
import {
  getCurrentUserId,
  getCurrentUser,
  rehydrateSessionFromLocal,
} from "../../../core/common/authSession";

/** ===== API shapes (Hotels) ===== */
type ApiCancelPolicy = {
  CancellationCharge: number;
  ChargeType: number; // 2 => Percentage
  Currency: string;
  FromDate: string;   // "07/11/2025 00:00:00"
  ToDate: string;     // "05/12/2025 23:59:59"
};

type ApiGuest = {
  city: string;
  contact_no: string;
  dob: string;
  email: string;
  first_name: string;
  gender: string;
  is_lead: number;
  last_name: string;
  nationality: string;
  title: string;
};

type ApiHotelBooking = {
  amount: number;
  booking_id: number;
  cancel_policies: ApiCancelPolicy[];
  cancellation_info: any | null;
  cancellation_policy: string;
  check_in: string;      // "06-12-2025"
  check_out: string;     // "08-12-2025"
  created_at: string;    // "08-11-2025 07:12"
  currency: string;      // "INR"
  guests: ApiGuest[];
  hotel_code: string;
  hotel_name: string;
  location: string;
  status: string;        // "Confirmed"
  tbo_booking_id: number;
};

/** ===== UI normalized (Hotels) ===== */
type UiHotelBooking = {
  AppBookingId: number;
  TboBookingId: number;
  HotelName: string;
  City: string;
  Location: string;
  CheckIn: string;   // ISO or original
  CheckOut: string;
  Nights: number;
  LeadGuest: string;
  GuestsCount: number;
  Amount: number;
  Currency: string;
  Status: string;
  CreatedAt: string;
  CancelPolicies: ApiCancelPolicy[];
  CancellationPolicyText: string;
  ChangeRequestId?: number;
};

/** ===== Flights (kept generic; adapt to your flights API if different) ===== */
type FlightBooking = {
  PNR?: string;
  BookingId?: number | string;
  From?: string;
  To?: string;
  Depart?: string;
  Return?: string;
  Pax?: number;
  Amount?: number;
  Currency?: string;
  Status?: string;
  [k: string]: any;
};

type ApiFlightBooking = {
  pnr: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  booking_id: number;

  airline: {
    name: string;
    iata_code: string;
  };

  passengers: any[];

  segments: Array<
    Array<{
      Origin: {
        Airport: {
          CityName: string;
          AirportCode: string;
        };
        DepTime: string;
      };
      Destination: {
        Airport: {
          CityName: string;
          AirportCode: string;
        };
        ArrTime: string;
      };
    }>
  >;

  /** ✅ ADD THESE */
  ticket?: {
    issued_at: string;
    pdf_path: string;
    status: string;
  };

  invoice?: {
    created_at: string;
    invoice_number: string;
    pdf_path: string;
  };

  // Added for cancellation flow
  booking_mode?: number;
  segments_data?: Array<{
    Origin: {
      Airport: {
        AirportCode: string;
        CityName: string;
      };
      DepTime: string;
    };
    Destination: {
      Airport: {
        AirportCode: string;
        CityName: string;
      };
      ArrTime: string;
    };
  }>;
};

type UiFlightBooking = {
  PNR: string;
  Airline: string;
  From: string;
  To: string;
  Depart: string;
  Arrival: string;
  Pax: number;
  Amount: number;
  Currency: string;
  Status: string;
  CreatedAt: string;
  TicketPdf?: string;
  InvoicePdf?: string;
  BookingId: string | number;
  BookingMode?: number;
  Segments?: Array<{
    OriginCode: string;
    OriginCity: string;
    DestinationCode: string;
    DestinationCity: string;
    DepartureTime: string;
    ArrivalTime: string;
  }>;
  ChangeRequestId?: number;
};

/** ===== Flight Cancellation Types ===== */
type CancellationCharges = {
  ChargeType: number;
  Currency: string;
  RefundAmount: number;
  ServiceCharge: number;
  ServiceTax: number;
  TotalCharge: number;
  Remarks?: string;
};

type CancellationResponse = {
  success: boolean;
  message?: string;
  charges?: CancellationCharges;
  changeRequestId?: number;
  status?: string;
  error?: string;
};

type FlightCancellationRequest = {
  action: 'AutoCancel' | 'GetCancellationCharges' | 'SendChangeRequest' | 'GetChangeRequestStatus' | 'ReleasePNR';
  BookingId: string | number;
  RequestType: 1 | 2; // 1: Full, 2: Partial
  CancellationType?: 3; // 3: Customer Request
  Remarks?: string;
  Origin?: string;
  Destination?: string;
  BookingMode?: number;
  ChangeRequestId?: number;
  Source?: string;
};

const ProfileSettings: React.FC = () => {
  const routes = all_routes;

  try {
    // rehydrateSessionFromLocal is safe to call synchronously (reads localStorage/sessionStorage)
    // If your implementation performs async work, do NOT call it here.
    rehydrateSessionFromLocal?.();
  } catch (err) {
    // swallow — rehydrate best-effort
    console.debug("rehydrateSessionFromLocal error:", err);
  }

  // USER (read after rehydrate)
  const userId = useMemo(() => getCurrentUserId?.() ?? null, []);
  const user = useMemo(() => getCurrentUser?.() ?? null, []);
  const userName = user?.name || "User";
  const userEmail = user?.email || "";

  // STATE (hotels)
  const [hotels, setHotels] = useState<UiHotelBooking[] | null>(null);
  const [hotelLoading, setHotelLoading] = useState(false);
  const [hotelError, setHotelError] = useState<string | null>(null);
  const [statusLoadingId, setStatusLoadingId] = useState<number | null>(null);
  const [cancelLoadingId, setCancelLoadingId] = useState<number | null>(null);
  const [policyOpen, setPolicyOpen] = useState<number | null>(null);

  // STATE (flights)
  const [flights, setFlights] = useState<UiFlightBooking[] | null>(null);
  const [flightLoading, setFlightLoading] = useState(false);
  const [flightError, setFlightError] = useState<string | null>(null);

  // STATE (Flight Cancellation)
  const [flightCancelLoading, setFlightCancelLoading] = useState<string | null>(null);
  const [flightStatusLoading, setFlightStatusLoading] = useState<number | null>(null);
  const [flightChargesLoading, setFlightChargesLoading] = useState<string | null>(null);
  const [cancellationCharges, setCancellationCharges] = useState<Record<string, CancellationCharges>>({});
  const [cancellationType, setCancellationType] = useState<Record<string, 'full' | 'partial'>>({});
  const [partialCancellationSector, setPartialCancellationSector] = useState<Record<string, { origin: string; destination: string }>>({});
  const [showCharges, setShowCharges] = useState<Record<string, boolean>>({});

  // Helpers
  const titleCase = (s?: string) =>
    (s || "").replace(/\w\S*/g, (t) => t[0].toUpperCase() + t.slice(1).toLowerCase());

  const parseDDMMYYYY = (s?: string): Date | null => {
    if (!s) return null;
    const m = s.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
    if (!m) return null;
    const [, dd, mm, yyyy] = m;
    const d = new Date(+yyyy, +mm - 1, +dd);
    return isNaN(d.getTime()) ? null : d;
  };
  const daysBetween = (a: Date | null, b: Date | null) => {
    if (!a || !b) return 0;
    const ms = b.getTime() - a.getTime();
    return Math.max(0, Math.round(ms / 86400000));
  };
  const decodePolicy = (raw: string) =>
    raw.replace(/\#\^\#/g, " — ").replace(/\|\#\!\#/g, "").trim();

  const toUiHotel = (row: ApiHotelBooking): UiHotelBooking => {
    const ci = parseDDMMYYYY(row.check_in);
    const co = parseDDMMYYYY(row.check_out);
    const lead = row.guests?.find((g) => +g.is_lead === 1) || row.guests?.[0];
    const leadName = lead
      ? `${lead.title ? lead.title + " " : ""}${titleCase(lead.first_name)} ${titleCase(lead.last_name)}`.trim()
      : "—";
    const city = row.guests?.[0]?.city || (row.location || "").split(",").slice(-1)[0]?.trim() || "";

    return {
      AppBookingId: row.booking_id,
      TboBookingId: row.tbo_booking_id,
      HotelName: row.hotel_name,
      City: city,
      Location: row.location,
      CheckIn: ci ? ci.toISOString() : row.check_in,
      CheckOut: co ? co.toISOString() : row.check_out,
      Nights: daysBetween(ci, co),
      LeadGuest: leadName,
      GuestsCount: Array.isArray(row.guests) ? row.guests.length : 0,
      Amount: row.amount,
      Currency: row.currency || "",
      Status: row.status || "—",
      CreatedAt: row.created_at || "",
      CancelPolicies: row.cancel_policies || [],
      CancellationPolicyText: row.cancellation_policy ? decodePolicy(row.cancellation_policy) : "",
    };
  };

  // Guards: do not call APIs if not logged in
  useEffect(() => {
    if (!userId) {
      alert("Please login to view your bookings.");
      setHotels([]);
      setFlights([]);
      setHotelError("Not logged in");
      setFlightError("Not logged in");
      return;
    }
    void (async () => {
      // Hotels
      try {
        setHotelLoading(true);
        setHotelError(null);
        const r = await fetch(API_URLS.HOTELS.MY_BOOKINGS, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const j = await r.json();
        const rows: ApiHotelBooking[] = Array.isArray(j) ? j : (j.bookings || j.data || []);
        setHotels(rows.map(toUiHotel));
      } catch (e: any) {
        setHotelError(e?.message || "Failed to load hotel bookings.");
        setHotels([]);
      } finally {
        setHotelLoading(false);
      }

      // Flights (optional if you have endpoint)
      if (!API_URLS.FLIGHTS?.MY_BOOKINGS) {
        setFlights([]);
        setFlightError(null);
        return;
      }
      try {
        setFlightLoading(true);
        setFlightError(null);
        const rf = await fetch(API_URLS.FLIGHTS.MY_BOOKINGS, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const jf = await rf.json();
        const rows: ApiFlightBooking[] = jf.bookings || [];
        setFlights(rows.map(toUiFlight));
      } catch (e: any) {
        setFlightError(e?.message || "Failed to load flight bookings.");
        setFlights([]);
      } finally {
        setFlightLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const toUiFlight = (row: ApiFlightBooking): UiFlightBooking => {
    const firstSegment = row.segments?.[0]?.[0];

    // Extract all segments for partial cancellation
    const segments = row.segments_data || row.segments?.flat() || [];
    const normalizedSegments = segments.map(seg => ({
      OriginCode: seg.Origin?.Airport?.AirportCode || '',
      OriginCity: seg.Origin?.Airport?.CityName || '',
      DestinationCode: seg.Destination?.Airport?.AirportCode || '',
      DestinationCity: seg.Destination?.Airport?.CityName || '',
      DepartureTime: seg.Origin?.DepTime || '',
      ArrivalTime: seg.Destination?.ArrTime || ''
    }));

    return {
      PNR: row.pnr,
      Airline: row.airline?.name || "—",
      From: firstSegment
        ? `${firstSegment.Origin.Airport.CityName} (${firstSegment.Origin.Airport.AirportCode})`
        : "—",
      To: firstSegment
        ? `${firstSegment.Destination.Airport.CityName} (${firstSegment.Destination.Airport.AirportCode})`
        : "—",
      Depart: firstSegment?.Origin?.DepTime || "",
      Arrival: firstSegment?.Destination?.ArrTime || "",
      Pax: row.passengers?.length || 0,
      Amount: row.amount,
      Currency: row.currency,
      Status: row.status,
      CreatedAt: row.created_at,
      TicketPdf: row.ticket?.pdf_path,
      InvoicePdf: row.invoice?.pdf_path,
      BookingId: row.booking_id,
      BookingMode: row.booking_mode,
      Segments: normalizedSegments,
    };
  };

  // Actions (Hotels)
  const cancelHotelBooking = async (row: UiHotelBooking) => {
    if (!userId) return alert("Please login to continue.");
    if (!row?.TboBookingId) return;

    setCancelLoadingId(row.TboBookingId);
    try {
      const res = await fetch(API_URLS.HOTELS.CANCELLATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "cancel",
          BookingId: row.TboBookingId, // TBO id as per your cURL
          Remarks: "Customer wants cancellation",
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setHotels((prev) =>
          (prev || []).map((b) =>
            b.TboBookingId === row.TboBookingId
              ? { ...b, Status: "Cancellation Requested", ChangeRequestId: data?.ChangeRequestId || b.ChangeRequestId }
              : b
          )
        );
      } else {
        alert(data?.message || data?.error || "Failed to request cancellation.");
      }
    } catch (e: any) {
      alert(e?.message || "Failed to request cancellation.");
    } finally {
      setCancelLoadingId(null);
    }
  };

  const checkHotelCancellationStatus = async (row: UiHotelBooking) => {
    if (!userId) return alert("Please login to continue.");
    if (!row?.ChangeRequestId) return alert("No ChangeRequestId for this booking.");

    setStatusLoadingId(row.ChangeRequestId);
    try {
      const res = await fetch(API_URLS.HOTELS.CANCELLATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "status",
          ChangeRequestId: row.ChangeRequestId,
          Remarks: "Customer wants cancellation",
        }),
      });
      const data = await res.json();
      if (res.ok) {
        const newStatus = data?.Status || data?.status || data?.message || "Cancellation status fetched";
        setHotels((prev) =>
          (prev || []).map((b) =>
            b.TboBookingId === row.TboBookingId ? { ...b, Status: String(newStatus) } : b
          )
        );
      } else {
        alert(data?.message || data?.error || "Failed to fetch status.");
      }
    } catch (e: any) {
      alert(e?.message || "Failed to fetch status.");
    } finally {
      setStatusLoadingId(null);
    }
  };

  // FLIGHT CANCELLATION ACTIONS
  const getFlightCancellationCharges = async (flight: UiFlightBooking) => {
    if (!userId) return alert("Please login to continue.");
    if (!flight.BookingId) return alert("No Booking ID found.");

    setFlightChargesLoading(flight.PNR);
    try {
      const requestData: FlightCancellationRequest = {
        action: 'GetCancellationCharges',
        BookingId: flight.BookingId,
        RequestType: cancellationType[flight.PNR] === 'partial' ? 2 : 1,
        BookingMode: flight.BookingMode || 5,
      };

      const res = await fetch(API_URLS.FLIGHTS?.CANCELLATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      const data: CancellationResponse = await res.json();

      if (res.ok && data.charges) {
        setCancellationCharges(prev => ({
          ...prev,
          [flight.PNR]: data.charges!
        }));
        setShowCharges(prev => ({
          ...prev,
          [flight.PNR]: true
        }));
      } else {
        alert(data?.message || data?.error || "Failed to get cancellation charges.");
      }
    } catch (e: any) {
      alert(e?.message || "Failed to get cancellation charges.");
    } finally {
      setFlightChargesLoading(null);
    }
  };

  const cancelFlightBooking = async (flight: UiFlightBooking) => {
    if (!userId) return alert("Please login to continue.");
    if (!flight.BookingId) return alert("No Booking ID found.");

    const isPartial = cancellationType[flight.PNR] === 'partial';
    const sector = partialCancellationSector[flight.PNR];

    if (isPartial && (!sector?.origin || !sector?.destination)) {
      return alert("Please select a sector for partial cancellation.");
    }

    const confirmMsg = isPartial
      ? `Are you sure you want to cancel sector ${sector.origin} to ${sector.destination}?`
      : "Are you sure you want to cancel the entire booking?";

    if (!window.confirm(confirmMsg)) return;

    setFlightCancelLoading(flight.PNR);
    try {
      const requestData: FlightCancellationRequest = {
        action: 'AutoCancel',
        BookingId: flight.BookingId,
        RequestType: isPartial ? 2 : 1,
        CancellationType: 3,
        Remarks: isPartial
          ? `Cancel sector ${sector.origin} to ${sector.destination}`
          : "Customer requested full cancellation",
      };

      if (isPartial && sector) {
        requestData.Origin = sector.origin;
        requestData.Destination = sector.destination;
      }

      const res = await fetch(API_URLS.FLIGHTS?.CANCELLATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      const data: CancellationResponse = await res.json();

      if (res.ok && data.success) {
        // Update flight status
        setFlights(prev =>
          (prev || []).map(f =>
            f.PNR === flight.PNR
              ? {
                ...f,
                Status: 'Cancellation Requested',
                ChangeRequestId: data.changeRequestId || f.ChangeRequestId
              }
              : f
          )
        );

        alert(data.message || "Cancellation request submitted successfully!");

        // Clear charges display
        setShowCharges(prev => ({ ...prev, [flight.PNR]: false }));
        setCancellationCharges(prev => {
          const newCharges = { ...prev };
          delete newCharges[flight.PNR];
          return newCharges;
        });
      } else {
        alert(data?.message || data?.error || "Failed to cancel booking.");
      }
    } catch (e: any) {
      alert(e?.message || "Failed to cancel booking.");
    } finally {
      setFlightCancelLoading(null);
    }
  };

  const checkFlightCancellationStatus = async (flight: UiFlightBooking) => {
    if (!userId) return alert("Please login to continue.");
    if (!flight.ChangeRequestId) return alert("No Change Request ID found for this booking.");

    setFlightStatusLoading(flight.ChangeRequestId);
    try {
      const requestData: FlightCancellationRequest = {
        action: 'GetChangeRequestStatus',
        ChangeRequestId: flight.ChangeRequestId,
        BookingId: flight.BookingId, // Still need BookingId in request
        RequestType: 1, // Default, not used for status check
      };

      const res = await fetch(API_URLS.FLIGHTS?.CANCELLATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      const data: CancellationResponse = await res.json();

      if (res.ok) {
        setFlights(prev =>
          (prev || []).map(f =>
            f.PNR === flight.PNR
              ? {
                ...f,
                Status: data.status || data.message || 'Status Updated'
              }
              : f
          )
        );
        alert(data.message || "Status updated successfully!");
      } else {
        alert(data?.message || data?.error || "Failed to get status.");
      }
    } catch (e: any) {
      alert(e?.message || "Failed to get status.");
    } finally {
      setFlightStatusLoading(null);
    }
  };

  const releaseFlightPNR = async (flight: UiFlightBooking) => {
    if (!userId) return alert("Please login to continue.");
    if (!flight.BookingId) return alert("No Booking ID found.");

    if (!window.confirm("Are you sure you want to release this PNR? This action cannot be undone.")) return;

    setFlightCancelLoading(flight.PNR);
    try {
      const requestData: FlightCancellationRequest = {
        action: 'ReleasePNR',
        BookingId: flight.BookingId,
        Source: 'API',
        RequestType: 1, // Required by API structure
      };

      const res = await fetch(API_URLS.FLIGHTS?.CANCELLATION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      const data: CancellationResponse = await res.json();

      if (res.ok && data.success) {
        setFlights(prev =>
          (prev || []).map(f =>
            f.PNR === flight.PNR
              ? { ...f, Status: 'PNR Released' }
              : f
          )
        );
        alert(data.message || "PNR released successfully!");
      } else {
        alert(data?.message || data?.error || "Failed to release PNR.");
      }
    } catch (e: any) {
      alert(e?.message || "Failed to release PNR.");
    } finally {
      setFlightCancelLoading(null);
    }
  };

  const handleCancellationTypeChange = (pnr: string, type: 'full' | 'partial') => {
    setCancellationType(prev => ({ ...prev, [pnr]: type }));
    // Clear sector selection when switching to full cancellation
    if (type === 'full') {
      setPartialCancellationSector(prev => {
        const newSectors = { ...prev };
        delete newSectors[pnr];
        return newSectors;
      });
    }
  };

  const handleSectorSelection = (pnr: string, origin: string, destination: string) => {
    setPartialCancellationSector(prev => ({
      ...prev,
      [pnr]: { origin, destination }
    }));
  };

  const breadcrumbs = [
    { label: "Profile", link: routes.home1, active: false },
    { label: "Profile", active: true },
  ];

  return (
    <div>
      <Breadcrumb title="Profile" breadcrumbs={breadcrumbs} backgroundClass="breadcrumb-bg-04" />

      <div className="content">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <div className="col-xl-3 col-lg-4">
              <Sidebar />
            </div>

            {/* Main */}
            <div className="col-xl-9 col-lg-8 theiaStickySidebar">
              {/* USER HEADER */}
              <div className="card mb-4">
                <div className="card-body d-flex align-items-center justify-content-between flex-wrap">
                  <div>
                    <h6 className="m-0">{userName}</h6>
                    <div className="text-muted small">
                      {userEmail || "—"} {userId ? `• ID: ${userId}` : ""}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-light btn-sm"
                      onClick={() => {
                        rehydrateSessionFromLocal?.();
                        window.location.reload();
                      }}
                    >
                      <i className="isax isax-refresh" /> Reload
                    </button>
                  </div>
                </div>
              </div>

              {/* BOOKINGS */}
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="m-0">Bookings</h6>
                  <div className="d-flex gap-2">
                    <button className="btn btn-light btn-sm" onClick={() => window.location.reload()}>
                      <i className="isax isax-refresh" /> Refresh
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  {/* HOTELS */}
                  <h6 className="fs-16 mb-3 d-flex align-items-center">
                    <i className="isax isax-building-4 me-2" /> Hotels
                  </h6>

                  {hotelLoading ? (
                    <p className="text-muted">Loading…</p>
                  ) : hotelError ? (
                    <p className="text-danger">{hotelError}</p>
                  ) : (hotels?.length || 0) === 0 ? (
                    <p className="text-muted">No hotel bookings found.</p>
                  ) : (
                    <div className="table-responsive mb-4">
                      <table className="table align-middle">
                        <thead>
                          <tr>
                            <th>App ID</th>
                            <th>TBO ID</th>
                            <th>Hotel / Location</th>
                            <th>City</th>
                            <th>Check-in</th>
                            <th>Check-out</th>
                            <th>Nights</th>
                            <th>Lead Guest</th>
                            <th>Guests</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th style={{ width: 260 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hotels!.map((b) => (
                            <React.Fragment key={b.AppBookingId}>
                              <tr>
                                <td className="fw-semibold">#{b.AppBookingId}</td>
                                <td>{b.TboBookingId}</td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span className="fw-semibold">{b.HotelName}</span>
                                    <span className="text-muted small">{b.Location}</span>
                                  </div>
                                </td>
                                <td>{b.City || "—"}</td>
                                <td>{b.CheckIn ? new Date(b.CheckIn).toLocaleDateString() : "—"}</td>
                                <td>{b.CheckOut ? new Date(b.CheckOut).toLocaleDateString() : "—"}</td>
                                <td>{b.Nights}</td>
                                <td>{b.LeadGuest}</td>
                                <td>{b.GuestsCount}</td>
                                <td>
                                  {b.Currency} {Number(b.Amount).toFixed(2)}
                                </td>
                                <td>
                                  <span className="badge badge-outline rounded-pill">{b.Status || "Confirmed"}</span>
                                </td>
                                <td>{b.CreatedAt || "—"}</td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={() => setPolicyOpen(policyOpen === b.AppBookingId ? null : b.AppBookingId)}
                                    >
                                      Policy
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      disabled={!!cancelLoadingId}
                                      onClick={() => cancelHotelBooking(b)}
                                    >
                                      {cancelLoadingId === b.TboBookingId ? "Cancelling…" : "Cancel"}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      disabled={!b.ChangeRequestId || !!statusLoadingId}
                                      onClick={() => checkHotelCancellationStatus(b)}
                                    >
                                      {statusLoadingId === b.ChangeRequestId ? "Checking…" : "Status"}
                                    </button>
                                  </div>
                                </td>
                              </tr>

                              {policyOpen === b.AppBookingId && (
                                <tr>
                                  <td colSpan={13} className="bg-light">
                                    <div className="p-3">
                                      <h6 className="mb-2">Cancellation Policy</h6>
                                      {b.CancellationPolicyText ? (
                                        <p className="mb-3">{b.CancellationPolicyText}</p>
                                      ) : (
                                        <p className="text-muted">No policy text available.</p>
                                      )}
                                      {b.CancelPolicies?.length > 0 && (
                                        <div className="table-responsive">
                                          <table className="table table-sm align-middle">
                                            <thead>
                                              <tr>
                                                <th>From</th>
                                                <th>To</th>
                                                <th>Charge</th>
                                                <th>Type</th>
                                                <th>Currency</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {b.CancelPolicies.map((p, i) => (
                                                <tr key={i}>
                                                  <td>{p.FromDate}</td>
                                                  <td>{p.ToDate}</td>
                                                  <td>{p.CancellationCharge}</td>
                                                  <td>{p.ChargeType === 2 ? "Percentage" : String(p.ChargeType)}</td>
                                                  <td>{p.Currency}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* FLIGHTS SECTION - Modern Card Design */}
                  <h6 className="fs-16 mb-3 d-flex align-items-center">
                    <i className="isax isax-airplane me-2" /> Flights
                  </h6>

                  {flightLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mt-2">Loading flight bookings...</p>
                    </div>
                  ) : flightError ? (
                    <div className="alert alert-danger">
                      <i className="isax isax-danger me-2" /> {flightError}
                    </div>
                  ) : (flights?.length || 0) === 0 ? (
                    <div className="card border">
                      <div className="card-body text-center py-5">
                        <i className="isax isax-airplane text-muted fs-1 mb-3"></i>
                        <h5 className="text-muted">No Flight Bookings</h5>
                        <p className="text-muted mb-0">
                          {API_URLS.FLIGHTS?.MY_BOOKINGS
                            ? "You haven't made any flight bookings yet."
                            : "Flight bookings feature is not configured."}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="row g-4">
                      {flights!.map((f) => (
                        <div key={f.PNR} className="col-12">
                          {/* Flight Booking Card */}
                          <div className="card border hover-shadow-sm">
                            <div className="card-body">
                              {/* Flight Header */}
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                  <div className="d-flex align-items-center gap-2 mb-1">
                                    <h6 className="mb-0 fw-bold">{f.Airline}</h6>
                                    <span className={`badge rounded-pill ${f.Status?.toLowerCase().includes('cancelled') ? 'bg-danger' :
                                        f.Status?.toLowerCase().includes('confirmed') ? 'bg-success' :
                                          f.Status?.toLowerCase().includes('pending') ? 'bg-warning' :
                                            'bg-secondary'
                                      }`}>
                                      {f.Status}
                                    </span>
                                  </div>
                                  <div className="text-muted small">
                                    <i className="isax isax-ticket me-1"></i>
                                    PNR: <span className="fw-semibold">{f.PNR}</span> •
                                    Booking ID: <span className="fw-semibold">{f.BookingId}</span>
                                  </div>
                                </div>
                                <div className="text-end">
                                  <div className="h4 fw-bold text-primary mb-0">
                                    {f.Currency} {f.Amount.toFixed(2)}
                                  </div>
                                  <div className="text-muted small">
                                    {f.Pax} {f.Pax === 1 ? 'Passenger' : 'Passengers'}
                                  </div>
                                </div>
                              </div>

                              {/* Flight Route */}
                              <div className="row align-items-center mb-4">
                                <div className="col-md-8">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="text-center">
                                      <div className="h6 fw-bold mb-1">{f.From?.split('(')[0]?.trim()}</div>
                                      <div className="text-muted small">{f.From?.match(/\(([^)]+)\)/)?.[1]}</div>
                                      <div className="text-muted small">
                                        {new Date(f.Depart).toLocaleDateString()} •
                                        {new Date(f.Depart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                    </div>
                                    <div className="flex-grow-1 mx-3 position-relative">
                                      <div className="dotted-line" style={{
                                        borderTop: "2px dashed #dee2e6",
                                        height: "1px",
                                        position: "relative"
                                      }}></div>
                                      <div className="text-center position-absolute top-50 start-50 translate-middle">
                                        <i className="isax isax-airplane text-primary bg-white p-1 rounded-circle"></i>
                                      </div>
                                    </div>
                                    <div className="text-center">
                                      <div className="h6 fw-bold mb-1">{f.To?.split('(')[0]?.trim()}</div>
                                      <div className="text-muted small">{f.To?.match(/\(([^)]+)\)/)?.[1]}</div>
                                      <div className="text-muted small">
                                        {new Date(f.Arrival).toLocaleDateString()} •
                                        {new Date(f.Arrival).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {/* <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                  <div className="d-flex flex-column gap-2">
                                    {f.TicketPdf && (
                                      <a
                                        href={f.TicketPdf}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-primary btn-sm"
                                      >
                                        <i className="isax isax-document-download me-2"></i>
                                        View Ticket
                                      </a>
                                    )}
                                    {f.InvoicePdf && (
                                      <a
                                        href={f.InvoicePdf}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-secondary btn-sm"
                                      >
                                        <i className="isax isax-receipt me-2"></i>
                                        View Invoice
                                      </a>
                                    )}
                                  </div>
                                </div> */}
                              </div>

                              {/* Action Buttons */}
                              <div className="border-top pt-3">
                                <div className="d-flex flex-wrap gap-2 justify-content-between">
                                  <div className="d-flex flex-wrap gap-2">
                                    {/* Manage Booking Button */}
                                    <button
                                      className="btn btn-primary btn-sm"
                                      onClick={() => {
                                        if (!cancellationType[f.PNR]) {
                                          setCancellationType(prev => ({ ...prev, [f.PNR]: 'full' }));
                                        }
                                        setShowCharges(prev => ({
                                          ...prev,
                                          [f.PNR]: !prev[f.PNR]
                                        }));
                                      }}
                                    >
                                      <i className="isax isax-setting-2 me-2"></i>
                                      Manage Booking
                                    </button>

                                    {/* Check Status Button */}
                                    {f.ChangeRequestId && (
                                      <button
                                        className="btn btn-outline-warning btn-sm"
                                        onClick={() => checkFlightCancellationStatus(f)}
                                        disabled={!!flightStatusLoading}
                                      >
                                        <i className="isax isax-status-up me-2"></i>
                                        {flightStatusLoading === f.ChangeRequestId ? "Checking..." : "Check Status"}
                                      </button>
                                    )}
                                  </div>

                                  {/* Quick Actions Dropdown */}
                                  <div className="dropdown">
                                    <button
                                      className="btn btn-outline-secondary btn-sm dropdown-toggle"
                                      type="button"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                    >
                                      <i className="isax isax-more"></i>
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-end">
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() => {
                                            if (window.confirm("Are you sure you want to release this PNR?")) {
                                              releaseFlightPNR(f);
                                            }
                                          }}
                                        >
                                          <i className="isax isax-unlock me-2"></i>
                                          Release PNR
                                        </button>
                                      </li>
                                      <li><hr className="dropdown-divider" /></li>
                                    </ul>
                                  </div>
                                </div>
                              </div>

                              {/* Cancellation Panel (Expanded) */}
                              {showCharges[f.PNR] && (
                                <div className="border-top mt-3 pt-3">
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0 fw-bold">
                                      <i className="isax isax-close-circle me-2 text-danger"></i>
                                      Cancellation Options
                                    </h6>
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => setShowCharges(prev => ({ ...prev, [f.PNR]: false }))}
                                    >
                                      <i className="isax isax-close"></i>
                                    </button>
                                  </div>

                                  {/* Cancellation Type Selection */}
                                  <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                      <div className={`card border ${cancellationType[f.PNR] === 'full' ? 'border-primary' : ''}`}>
                                        <div className="card-body">
                                          <div className="form-check mb-0">
                                            <input
                                              className="form-check-input"
                                              type="radio"
                                              name={`cancellationType-${f.PNR}`}
                                              id={`full-${f.PNR}`}
                                              checked={cancellationType[f.PNR] === 'full'}
                                              onChange={() => handleCancellationTypeChange(f.PNR, 'full')}
                                            />
                                            <label className="form-check-label w-100" htmlFor={`full-${f.PNR}`}>
                                              <div className="d-flex align-items-center">
                                                <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                                  <i className="isax isax-danger text-primary fs-4"></i>
                                                </div>
                                                <div>
                                                  <h6 className="mb-1">Full Cancellation</h6>
                                                  <p className="text-muted small mb-0">Cancel the entire booking including all passengers</p>
                                                </div>
                                              </div>
                                            </label>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className={`card border ${cancellationType[f.PNR] === 'partial' ? 'border-primary' : ''} ${(!f.Segments || f.Segments.length <= 1) ? 'opacity-50' : ''}`}>
                                        <div className="card-body">
                                          <div className="form-check mb-0">
                                            <input
                                              className="form-check-input"
                                              type="radio"
                                              name={`cancellationType-${f.PNR}`}
                                              id={`partial-${f.PNR}`}
                                              checked={cancellationType[f.PNR] === 'partial'}
                                              onChange={() => handleCancellationTypeChange(f.PNR, 'partial')}
                                              disabled={!f.Segments || f.Segments.length <= 1}
                                            />
                                            <label className="form-check-label w-100" htmlFor={`partial-${f.PNR}`}>
                                              <div className="d-flex align-items-center">
                                                <div className="bg-warning bg-opacity-10 p-2 rounded me-3">
                                                  <i className="isax isax-chart text-warning fs-4"></i>
                                                </div>
                                                <div>
                                                  <h6 className="mb-1">Partial Cancellation</h6>
                                                  <p className="text-muted small mb-0">
                                                    {(!f.Segments || f.Segments.length <= 1)
                                                      ? "Only available for multi-sector bookings"
                                                      : "Cancel only specific sectors of the journey"}
                                                  </p>
                                                </div>
                                              </div>
                                            </label>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Sector Selection for Partial */}
                                  {cancellationType[f.PNR] === 'partial' && f.Segments && f.Segments.length > 1 && (
                                    <div className="mb-4">
                                      <h6 className="mb-3">Select Sector to Cancel</h6>
                                      <div className="row g-3">
                                        {f.Segments.map((segment, segIdx) => (
                                          <div key={segIdx} className="col-md-6">
                                            <div className={`card border hover-shadow-sm ${partialCancellationSector[f.PNR]?.origin === segment.OriginCode &&
                                                partialCancellationSector[f.PNR]?.destination === segment.DestinationCode
                                                ? 'border-primary border-2' : ''
                                              }`}>
                                              <div className="card-body">
                                                <div className="form-check mb-0">
                                                  <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name={`sector-${f.PNR}`}
                                                    id={`sector-${f.PNR}-${segIdx}`}
                                                    checked={
                                                      partialCancellationSector[f.PNR]?.origin === segment.OriginCode &&
                                                      partialCancellationSector[f.PNR]?.destination === segment.DestinationCode
                                                    }
                                                    onChange={() => handleSectorSelection(
                                                      f.PNR,
                                                      segment.OriginCode,
                                                      segment.DestinationCode
                                                    )}
                                                  />
                                                  <label className="form-check-label w-100" htmlFor={`sector-${f.PNR}-${segIdx}`}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                      <div>
                                                        <span className="fw-bold">{segment.OriginCity}</span>
                                                        <i className="isax isax-arrow-right-3 mx-2 text-muted"></i>
                                                        <span className="fw-bold">{segment.DestinationCity}</span>
                                                      </div>
                                                      <span className="badge bg-light text-dark">Sector {segIdx + 1}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between text-muted small">
                                                      <div>
                                                        <i className="isax isax-location me-1"></i>
                                                        {segment.OriginCode} → {segment.DestinationCode}
                                                      </div>
                                                      <div>
                                                        <i className="isax isax-calendar me-1"></i>
                                                        {new Date(segment.DepartureTime).toLocaleDateString()}
                                                      </div>
                                                    </div>
                                                  </label>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      {!partialCancellationSector[f.PNR] && (
                                        <div className="alert alert-warning mt-3">
                                          <i className="isax isax-warning-2 me-2"></i>
                                          Please select a sector to proceed with partial cancellation
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Charges Display or Calculate Button */}
                                  {cancellationCharges[f.PNR] ? (
                                    <div className="card border-primary">
                                      <div className="card-body">
                                        <div className="row align-items-center">
                                          <div className="col-md-8">
                                            <h6 className="mb-3">
                                              <i className="isax isax-money-send me-2 text-success"></i>
                                              Cancellation Charges
                                            </h6>
                                            <div className="row">
                                              <div className="col-6">
                                                <div className="mb-3">
                                                  <div className="text-muted small">Total Deductions</div>
                                                  <div className="h4 fw-bold text-danger">
                                                    {cancellationCharges[f.PNR].Currency} {cancellationCharges[f.PNR].TotalCharge.toFixed(2)}
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="col-6">
                                                <div className="mb-3">
                                                  <div className="text-muted small">Refund Amount</div>
                                                  <div className="h4 fw-bold text-success">
                                                    {cancellationCharges[f.PNR].Currency} {cancellationCharges[f.PNR].RefundAmount.toFixed(2)}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                            {cancellationCharges[f.PNR].Remarks && (
                                              <div className="alert alert-info mb-0">
                                                <i className="isax isax-info-circle me-2"></i>
                                                {cancellationCharges[f.PNR].Remarks}
                                              </div>
                                            )}
                                          </div>
                                          <div className="col-md-4">
                                            <div className="d-grid gap-2">
                                              <button
                                                className="btn btn-danger btn-lg"
                                                onClick={() => cancelFlightBooking(f)}
                                                disabled={!!flightCancelLoading ||
                                                  (cancellationType[f.PNR] === 'partial' && !partialCancellationSector[f.PNR])}
                                              >
                                                {flightCancelLoading === f.PNR ? (
                                                  <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Processing...
                                                  </>
                                                ) : (
                                                  <>
                                                    <i className="isax isax-close-circle me-2"></i>
                                                    Confirm Cancellation
                                                  </>
                                                )}
                                              </button>
                                              <button
                                                className="btn btn-outline-secondary"
                                                onClick={() => {
                                                  setCancellationCharges(prev => {
                                                    const newCharges = { ...prev };
                                                    delete newCharges[f.PNR];
                                                    return newCharges;
                                                  });
                                                }}
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <button
                                        className="btn btn-primary px-4"
                                        onClick={() => getFlightCancellationCharges(f)}
                                        disabled={
                                          !!flightChargesLoading ||
                                          (cancellationType[f.PNR] === 'partial' && !partialCancellationSector[f.PNR])
                                        }
                                      >
                                        {flightChargesLoading === f.PNR ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Calculating Charges...
                                          </>
                                        ) : (
                                          <>
                                            <i className="isax isax-calculator me-2"></i>
                                            Calculate Cancellation Charges
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* /BOOKINGS */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;